import React from 'react';
import { IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius, getControlBorder, getControlBorderColor } from '../../../theme/theme';
import { useOptionalThemeContext } from '../../providers/ThemeContext';

interface ThemeModeToggleProps {
  compact?: boolean;
  sx?: SxProps<Theme>;
}

const ThemeModeToggle: React.FC<ThemeModeToggleProps> = ({ compact = false, sx }) => {
  const theme = useTheme();
  const themeContext = useOptionalThemeContext();
  const mode = themeContext?.mode ?? theme.palette.mode;
  const toggleTheme = themeContext?.toggleTheme ?? (() => undefined);
  const nextMode = mode === 'light' ? 'dark' : 'light';
  const label = `Switch to ${nextMode} mode`;
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={toggleTheme}
        aria-label={label}
        aria-pressed={mode === 'dark' ? 'true' : 'false'}
        size="small"
        sx={[
          {
            flexShrink: 0,
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            width: compact ? 24 : 26,
            height: compact ? 24 : 26,
            p: 0.25,
            borderRadius: `${borderRadius.sm}px`,
            border: getControlBorder(theme),
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              borderColor: getControlBorderColor(theme),
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderColor: getControlBorderColor(theme),
            },
            transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
          },
          ...sxOverrides,
        ]}
      >
        {mode === 'light' ? (
          <DarkModeIcon sx={{ fontSize: compact ? 14 : 15 }} />
        ) : (
          <LightModeIcon sx={{ fontSize: compact ? 14 : 15 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeModeToggle;
